import { supabase } from "../config/supabaseClient";
import { getSdfUserByUserId } from "../slices/sdfSlice";
import { setSdfUserData } from "../utils/roleUtils";
import { store } from "../store";

const AUTH_SYNC_RELOAD_FLAG = "authSync_reloading";

/**
 * Validates that localStorage sdfUser matches the current Supabase auth user (JWT).
 * Only acts when there is a mismatch: refetches sdfUser and updates localStorage.
 * For all other scenarios (match, no auth, refetch fails), does nothing.
 *
 * Prevents: sdfUser showing one user while API requests use another, causing RLS to return empty data.
 */
export async function validateAndSyncSdfUser() {
  try {
    // Prevent infinite reload loops - if we just reloaded, skip sync check
    // This flag is set before reload and cleared after reload to prevent loops
    if (sessionStorage.getItem(AUTH_SYNC_RELOAD_FLAG)) {
      sessionStorage.removeItem(AUTH_SYNC_RELOAD_FLAG);
      return { synced: true, action: "ok", skipped: true };
    }

    const { data: { user: authUser } } = await supabase.auth.getUser();
    let sdfUser = null;
    try {
      const sdfUserStr = localStorage.getItem("sdfUser");
      sdfUser = sdfUserStr ? JSON.parse(sdfUserStr) : null;
    } catch (error) {
      // Malformed sdfUser: treat as missing, do nothing
      return { synced: true, action: "ok" };
    }

    // No auth or no sdfUser: do nothing
    if (!authUser || !sdfUser) {
      return { synced: true, action: "ok" };
    }

    const authUserId = authUser?.id;
    const sdfUserId = sdfUser?.user_id; // user_id links to auth.users; .id is company_user row id

    // Match: do nothing (require both to be truthy to avoid false matches)
    if (authUserId && sdfUserId && sdfUserId === authUserId) {
      return { synced: true, action: "ok" };
    }

    // Invalid authUserId: do nothing (safety guard)
    if (!authUserId) {
      return { synced: true, action: "ok" };
    }

    // Mismatch only: refetch sdfUser for current auth user
    const result = await store.dispatch(getSdfUserByUserId(authUserId));

    if (result.meta?.requestStatus === "fulfilled" && result.payload) {
      // Update localStorage with fresh sdfUser data
      setSdfUserData(result.payload);
      
      // Sync token and user data from stored auth token
      const authTokenKey = process.env.REACT_APP_SUPABASE_ACCESS_TOKEN;
      if (authTokenKey) {
        const stored = localStorage.getItem(authTokenKey);
        if (stored) {
          try {
            const parsed = JSON.parse(stored);
            if (parsed?.access_token) localStorage.setItem("token", parsed.access_token);
            if (parsed?.user) localStorage.setItem("user", JSON.stringify(parsed.user));
          } catch (error) {
            // ignore malformed stored auth token
          }
        }
      }

      // Set flag to prevent reload loop, then reload
      sessionStorage.setItem(AUTH_SYNC_RELOAD_FLAG, "true");
      window.location.reload();
      return { synced: true, action: "refetched" };
    }

    // Refetch failed (network error, etc). Do NOT sign out - avoid kicking valid users.
    // User stays in current state; mismatch may persist until next successful login.
    return { synced: false, action: "refetch_failed", error: result?.error };
  } catch (error) {
    return { synced: false, error: error.message };
  }
}
